import { Notification } from '@workspace/lib/api'

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'error' | 'reconnecting'

/**
 * Socket namespace constants
 * Use these instead of hardcoding namespace strings
 */
export const SOCKET_NAMESPACES = {
    ROOT: '/',
    NOTIFICATIONS: '/notifications',
} as const

export type SocketNamespace = (typeof SOCKET_NAMESPACES)[keyof typeof SOCKET_NAMESPACES]

/**
 * Socket authentication options
 */
export interface SocketAuthOptions {
    auth?: {
        token: string
    }
}

/**
 * Socket.io client options
 */
export interface SocketOptions extends SocketAuthOptions {
    autoConnect?: boolean
    reconnection?: boolean
    reconnectionAttempts?: number
    reconnectionDelay?: number
    [key: string]: any
}

export interface SocketConfiguration {
    url: string
    options?: SocketOptions
    namespace?: string
    autoConnect?: boolean
}

export type SocketProgressStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PENDING'

/**
 * Upload progress event payload
 */
export interface UploadProgressPayload {
    fileId: string
    fileName: string
    percentage: number
    status: SocketProgressStatus
    timestamp: string
}

/**
 * OCR update event payload
 */
export interface OcrUpdatePayload {
    fileId: string
    fileName: string
    documentId: string
    attachmentId: string
    status: SocketProgressStatus
    percentage: number
    index: number
    timestamp: string
}

/**
 * Define your application's event schema here.
 * Format: [EventName]: [PayloadType]
 */
/**
 * Notification socket event payload from backend
 * Uses the exact Notification type from @workspace/lib/api/notification
 * Event: client.emit('notification', { notification: data.notification })
 */
export interface NotificationSocketPayload {
    notification: Notification
}

export interface EventSchema {
    notification: NotificationSocketPayload
    'document:updated': { documentId: string; userId: string; timestamp: string }
    subscribe: { fileId: string }
    upload_progress: UploadProgressPayload
    ocr_update: OcrUpdatePayload
    suggestion_update: OcrUpdatePayload
    cad_update: OcrUpdatePayload
    subscribed: { fileId: string }
    error: { message: string; code?: string }
    [key: string]: any
}

export type EventNames = keyof EventSchema & string

export interface SocketState {
    isConnected: boolean
    status: ConnectionStatus
    error: Error | null
}
